import { useEffect, useRef, useState } from 'react';
import { ConnectionActivity } from './connect-access/activity';
import { useCompany } from '@/company/context';
import { writeWorkspaceHash } from '@/lib/chat-links';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import accessCatalog from '@/i18n/ns/connection-access';
import connectionsCatalog from '@/i18n/ns/connections';
import { useWorkspace } from './context';
import { connectionKey, type ScopedConnectionItem } from './connection-api';
import { LoadingState, PageHeading } from './primitives';
import { WorkspaceIcon } from './icons';
import { useConnections } from './use-connections';
import { PipedreamCatalogue } from './pipedream-catalogue';
import { isPipedreamApp, type CatalogueEntry } from './pipedream-catalogue-api';
import type { CatalogueConnection } from './catalogue-card';
import { ConnectionFeedback } from './connection-feedback';
import {
  connectionAvailabilityMessage,
  connectionReadinessMessage,
} from './connection-availability';
import { useAgentFeatures } from './agent-features';
import { useCustomMcp } from './custom-mcp';
import { shopifySetupRequested, useShopifyDevAppSheet } from './custom-mcp/shopify-catalogue';
import { canManageDatabase, useDatabases } from './connection-read-access';
import { QueryLimitControl } from './connection-read-access/query-limit';
import type { ConnectionReadItem } from './connection-read-access/api';
import { NativePostgresSetup } from './native-postgres-setup';
import { CustomerAppSetup, type CustomerAppProgress } from './customer-app-setup';
import { ConnectSheetHost } from './connect-access/connect-sheet';
import { useConnectionContextKey } from './connect-access/use-current-request';
import type { AccessAction } from './connect-access/policy-sheet';
import { registerCodingAgentsSource } from './agent-connections/coding-agents-source';
import { createCodingAgentsHttpSource } from './agent-connections/coding-agents-http.mjs';
import {
  accountRow,
  databaseRow,
  isConnectedAccount,
  serverRow,
  sortRows,
  type ConnectionRow,
} from './your-connections/rows';
import { YourConnections } from './your-connections/list';
import { ConnectionPanel, type PanelController, type PanelTab } from './your-connections/panel';
import { useAppLogos } from './your-connections/use-app-logos';
import { rowIdentity, useConnectionWords } from './your-connections/words';
import {
  OtherAssistantsSheet,
  agentRequested,
  assistantsSetupRequested,
} from './your-connections/other-assistants';
import { useIntlLocale } from './intl-locale';

// One root source per Connections module, before child effects read it.
// The HTTP producer performs current cookie/company authorization on every call.
registerCodingAgentsSource(createCodingAgentsHttpSource());

/**
 * Connections: everything connected in one list, then the catalogue to add more. Each source keeps
 * its own data and actions (accounts, the person's own servers, databases); the page shows them as
 * rows of one list, and a row opens its panel.
 */
export function ConnectionsPage() {
  const company = useCompany().data;
  const words = useConnectionWords(useWorkspace().companyName);
  // The one boundary for every protected read below: they start for the committed account and
  // company. A direct deep link renders this page before the company bootstrap answers, and a read
  // sent then has no identity pair. A switch of account, company or role starts the page over.
  const resolved = Boolean(company?.company?.id && company.membership?.userId);
  const contextKey = useConnectionContextKey();
  if (!resolved) return <LoadingState label={words.copy.yoursLoading} />;
  return <ConnectionsPageContent key={contextKey} />;
}

type PanelState = {
  key: string;
  tab?: PanelTab;
  action?: AccessAction | null;
  /** Counts deep-link opens, so a panel already open starts again on the requested tab. */
  opened?: number;
};

// `?access=<connectionId>` (an email draft card or an agent's setup on a connection set to Read)
// opens that connection's panel on its access, where writing is turned on (D73).
function accessRequested() {
  const query = typeof location === 'undefined' ? '' : (location.hash.split('?')[1] ?? '');
  return new URLSearchParams(query).get('access');
}
// The address without the request, so the same link can open the panel again.
function withoutAccessRequest() {
  const [route, query = ''] = location.hash.split('?');
  const params = new URLSearchParams(query);
  params.delete('access');
  const rest = params.toString();
  return rest ? `${route}?${rest}` : route;
}
type CustomerAppFlow = {
  items: ScopedConnectionItem[];
  initialId: string;
  started: { id: string; audience: ScopedConnectionItem['audience'] } | null;
};

function ConnectionsPageContent() {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const company = useCompany().data;
  const words = useConnectionWords(ws.companyName);
  const { copy } = words;
  const accessCopy = useNamespaceCopy(accessCatalog);
  const connectionsCopy = useNamespaceCopy(connectionsCatalog);
  const features = useAgentFeatures();
  const connections = useConnections(ws.companyName);
  const role = company?.membership?.role ?? '';
  const companyAdmin = ['owner', 'admin'].includes(role);
  const [activity, setActivity] = useState(false);
  const [panel, setPanel] = useState<PanelState | null>(null);
  const [accessRequest, setAccessRequest] = useState(accessRequested);
  const [assistants, setAssistants] = useState(
    () => assistantsSetupRequested() || agentRequested() !== null,
  );
  const [customerApp, setCustomerApp] = useState<CustomerAppFlow | null>(null);
  const [managedDatabase, setManagedDatabase] = useState<ConnectionReadItem | null>(null);
  const [notice, setNotice] = useState('');
  // The catalogue and the person's own servers refresh each other when one is added or removed.
  const [customMcpRefresh, setCustomMcpRefresh] = useState({ version: 0, notice: '' });
  const [catalogueRefresh, setCatalogueRefresh] = useState(0);
  const { data } = connections;
  const custom = useCustomMcp({
    managedConnectionIds: data?.items.flatMap((item) =>
      item.mcpConnectionId ? [item.mcpConnectionId] : [],
    ),
    refresh: customMcpRefresh,
    onChanged: () => setCatalogueRefresh((value) => value + 1),
  });
  const databases = useDatabases(true);
  const shopify = useShopifyDevAppSheet({
    companyName: ws.companyName,
    canConnectCompany: custom.canConnectCompany,
    onConnected: (message) => {
      setCatalogueRefresh((value) => value + 1);
      setCustomMcpRefresh((current) => ({ version: current.version + 1, notice: message }));
    },
  });
  // A chat offer to connect Shopify links here with ?setup=shopify: the sheet opens once the page
  // knows a server may be added, like the catalogue card's own Connect.
  const [shopifyRequested, setShopifyRequested] = useState(shopifySetupRequested);
  useEffect(() => {
    if (!shopifyRequested || !custom.canConnect) return;
    setShopifyRequested(false);
    shopify.open('private');
  }, [shopifyRequested, custom.canConnect]);

  // Customer-owned apps (Meta) connect and disconnect through this page's own flow, so their
  // catalogue tiles re-read whenever this page's list shows one of them change.
  const customerAppState = (data?.items ?? [])
    .filter((item) => item.appSetup)
    .map((item) => `${item.audience}:${item.id}:${item.status}`)
    .join('|');
  const seenCustomerApps = useRef<string | null>(null);
  useEffect(() => {
    if (!data) return;
    if (seenCustomerApps.current !== null && seenCustomerApps.current !== customerAppState)
      setCatalogueRefresh((value) => value + 1);
    seenCustomerApps.current = customerAppState;
  }, [data, customerAppState]);

  // A reconnect or a "Try again" of a customer-owned app opens its own setup, past the choice.
  const requestedCustomerApp = connections.customerApp;
  useEffect(() => {
    if (!requestedCustomerApp) return;
    setCustomerApp({
      items: [requestedCustomerApp],
      initialId: requestedCustomerApp.id,
      started: null,
    });
    connections.closeCustomerApp();
  }, [requestedCustomerApp]);

  // Knowledge and the chat card link here with ?setup=knowledge-client, a Shopify offer with
  // ?setup=shopify.
  useEffect(() => {
    const onHash = () => {
      if (assistantsSetupRequested() || agentRequested() !== null) setAssistants(true);
      if (shopifySetupRequested()) setShopifyRequested(true);
      setAccessRequest(accessRequested());
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const rows = sortRows([
    ...(data?.items ?? [])
      .filter(isConnectedAccount)
      .map((item) => accountRow(item, connectionKey(item))),
    ...custom.items.map(serverRow),
    ...(databases.items ?? []).map(databaseRow),
  ]);
  const logoFor = useAppLogos(rows.map((row) => row.appSlug));
  const openPanel = (row: ConnectionRow, tab?: PanelTab, action: AccessAction | null = null) =>
    setPanel({ key: row.key, tab, action });
  // The requested connection's panel opens once its row has arrived.
  const requestedRow = accessRequest
    ? rows.find((row) => row.read?.connectionId === accessRequest)
    : undefined;
  useEffect(() => {
    if (!requestedRow) return;
    setAccessRequest(null);
    writeWorkspaceHash(withoutAccessRequest(), 'replace');
    setPanel((prior) => ({
      key: requestedRow.key,
      tab: 'access',
      action: null,
      opened: (prior?.opened ?? 0) + 1,
    }));
  }, [requestedRow?.key]);

  const signInFor = (row: ConnectionRow): (() => void) | null => {
    // Signing a server in again is for whoever can manage it; the server refuses anyone else.
    if (row.server)
      return row.server.status === 'needs_authorization' && row.server.canManage
        ? () => void custom.run(row.server!.id, 'authorize')
        : null;
    const item = row.item;
    if (!item?.actions.connect.available) return null;
    if (item.appSetup)
      return () => setCustomerApp({ items: [item], initialId: item.id, started: null });
    return () => connections.requestConnect(item);
  };

  // A native service's account for the other audience (the company's, or the person's own).
  const otherAudience = (item: ScopedConnectionItem) => {
    const other = data?.items.find(
      (option) =>
        option.id === item.id &&
        option.audience !== item.audience &&
        !isConnectedAccount(option) &&
        option.actions.connect.available,
    );
    return other ? () => connections.requestConnect(other) : undefined;
  };

  // Each way a customer-owned app can be connected, once, as the catalogue offers it: the person's
  // own while it can still connect, else the company's.
  const customerAppChoices = (kind: string) => {
    const choices = new Map<string, ScopedConnectionItem>();
    for (const option of data?.items ?? []) {
      if (option.appSetup?.kind !== kind) continue;
      const prior = choices.get(option.id);
      if (!prior || (option.audience === 'private' && option.actions.connect.available))
        choices.set(option.id, option);
    }
    return [...choices.values()];
  };
  // Another way into a customer-owned app (Meta's other use case) opens its setup at the choice.
  const anotherCustomerApp = (item: ScopedConnectionItem) => {
    const choices = customerAppChoices(item.appSetup!.kind);
    const open = choices.find(
      (option) => option.status !== 'connected' && option.actions.connect.available,
    );
    return open
      ? () => setCustomerApp({ items: choices, initialId: open.id, started: null })
      : otherAudience(item);
  };

  const controllerFor = (row: ConnectionRow): PanelController => {
    if (row.server) {
      const server = row.server;
      return {
        busy: custom.busy === server.id,
        test: server.canManage ? () => void custom.run(server.id, 'test') : undefined,
        signIn: signInFor(row) ?? undefined,
        addAnother:
          server.authType === 'shopify_dev_app' && custom.canConnect
            ? () => shopify.open('private')
            : undefined,
        remove: (fetchImpl) => custom.remove(server.id, fetchImpl),
        removeMessage:
          server.authType === 'shopify_dev_app' ? accessCopy.removeCredentialsConfirm : undefined,
        onSaved: custom.setReadAccess,
        onStale: () => void custom.refresh(),
        tools: server.tools
          ?.filter((tool) => tool.title)
          .map((tool) => ({ title: tool.title!, effect: tool.effect ?? 'write' })),
        problem: custom.problemFor(server),
      };
    }
    if (row.kind === 'database' && row.read) {
      const read = row.read;
      return {
        busy: false,
        onSaved: databases.update,
        onStale: () => void databases.load(),
        extra: (
          <section className="ws-conn-panel-section ws-conn-database">
            {companyAdmin && <QueryLimitControl connectionId={read.connectionId} />}
            {canManageDatabase(read, companyAdmin) && (
              <button
                type="button"
                className="ws-text-button"
                onClick={() => {
                  setPanel(null);
                  setManagedDatabase(read);
                }}
              >
                {copy.manageDatabase}
              </button>
            )}
          </section>
        ),
      };
    }
    const item = row.item!;
    const canDisconnect = item.actions.disconnect.available && Boolean(item.connectionId);
    const pipedream = item.id.startsWith('pipedream:');
    return {
      busy: connections.busy === row.key,
      signIn: signInFor(row) ?? undefined,
      addAnother: item.appSetup
        ? anotherCustomerApp(item)
        : !pipedream
          ? otherAudience(item)
          : item.actions.addAnother?.available === true && connections.sheetAudiences.canPrivate
            ? () =>
                connections.requestConnect({
                  ...item,
                  audience: 'private',
                  status: 'not_connected',
                  connectionId: null,
                  accountLabel: null,
                  connectedAt: null,
                  lastUsedAt: null,
                  readAccess: undefined,
                  actions: { connect: { available: true }, disconnect: { available: false } },
                })
            : undefined,
      remove:
        canDisconnect && row.read
          ? async () => {
              await connections.disconnect(item, true);
            }
          : undefined,
      disconnect: canDisconnect && !row.read ? () => connections.disconnect(item, true) : undefined,
      onSaved: connections.setReadAccess,
      onStale: connections.retry,
      detail:
        connectionReadinessMessage(item.readiness, locale) ??
        (item.status === 'unavailable'
          ? connectionAvailabilityMessage(item.lastError ?? undefined, locale)
          : null),
    };
  };

  // Catalogue tiles say where the person stands: the row behind an entry, the one needing them first.
  const rowFor = (entry: CatalogueEntry) =>
    entry.provider === 'customer_app'
      ? rows.find((row) => row.item?.id === entry.item.id)
      : entry.provider === 'shopify_dev_app'
        ? rows.find((row) => row.server?.authType === 'shopify_dev_app')
        : isPipedreamApp(entry)
          ? rows.find((row) => row.appSlug === entry.slug)
          : undefined;
  const connectionFor = (entry: CatalogueEntry): CatalogueConnection | null => {
    const row = rowFor(entry);
    return row
      ? {
          state: row.state,
          identity: rowIdentity(row, words),
          open: () => openPanel(row),
          signIn: signInFor(row) ?? undefined,
        }
      : null;
  };

  // Installations without Pipedream offer their native services in the catalogue: one tile per
  // service, the person's own account first; the connect sheet chooses who can use it.
  const nativeOffers =
    data && !data.pipedreamAvailable
      ? [...new Set(data.items.filter((item) => !item.appSetup).map((item) => item.id))].map(
          (id) => {
            const options = data.items.filter((item) => item.id === id);
            const item =
              options.find(
                (option) => option.audience === 'private' && option.actions.connect.available,
              ) ??
              options.find((option) => option.actions.connect.available) ??
              options[0];
            const row = rows.find((candidate) => candidate.item?.id === id);
            return {
              item,
              connection: row
                ? {
                    state: row.state,
                    identity: rowIdentity(row, words),
                    open: () => openPanel(row),
                    signIn: signInFor(row) ?? undefined,
                  }
                : null,
            };
          },
        )
      : [];

  // The customer-owned app's progress once the person continued to its provider.
  const started = customerApp?.started;
  const startedKey = started ? `${started.audience}:${started.id}` : null;
  const startedItem = started
    ? data?.items.find((item) => item.id === started.id && item.audience === started.audience)
    : undefined;
  const customerProgress: CustomerAppProgress | null = !started
    ? null
    : connections.busy === startedKey
      ? { phase: 'waiting', connectLink: connections.connectLink }
      : connections.open === startedKey &&
          startedItem?.status === 'connected' &&
          !connections.failure
        ? { phase: 'done', item: startedItem }
        : null;
  // A failure is explained by the feedback dialog; a stop or an expired wait just closes.
  useEffect(() => {
    if (started && !customerProgress) setCustomerApp(null);
  }, [started, customerProgress]);

  const pipedreamSetup = data?.pipedreamSetup ?? data?.setup;
  const current = panel ? rows.find((row) => row.key === panel.key) : undefined;
  const sendToChat = (prompt: string) => ws.send(prompt, undefined, true, null, { newChat: true });
  const notices = [notice, connections.notice, custom.notice?.text ?? ''].filter(Boolean);
  const blockedLink = connections.connectLink || custom.authorizationUrl;
  // Shown on the page, and in the panel while it covers the page.
  const feedback =
    notices.length || blockedLink ? (
      <>
        {notices.map((text) => (
          <p key={text} className="ws-connection-notice" role="status">
            {text}
          </p>
        ))}
        {blockedLink && (
          <p className="ws-connection-notice" role="status">
            {copy.popupBlocked}{' '}
            <a href={blockedLink} target="_blank" rel="noreferrer">
              {copy.popupOpen}
            </a>{' '}
            {copy.popupKeepChecking}
          </p>
        )}
      </>
    ) : null;
  return (
    <div className="ws-connections-page">
      <ConnectionFeedback connections={connections} />
      <ConnectSheetHost connections={connections} companyName={ws.companyName} />
      {customerApp && (
        <CustomerAppSetup
          key={`${customerApp.initialId}:${customerApp.items.length}`}
          items={customerApp.items}
          initialId={customerApp.initialId}
          companyName={ws.companyName}
          {...connections.customerAppAudiences}
          progress={customerProgress}
          onClose={() => setCustomerApp(null)}
          onContinue={(target, choice) => {
            setCustomerApp((flow) =>
              flow ? { ...flow, started: { id: target.id, audience: target.audience } } : flow,
            );
            connections.continueConnect(target, choice);
          }}
          onStopWaiting={() => {
            connections.cancel();
            setCustomerApp(null);
          }}
          onAsk={sendToChat}
        />
      )}
      {current && (
        <ConnectionPanel
          key={`${current.key}:${panel?.opened ?? 0}`}
          row={current}
          controller={controllerFor(current)}
          words={words}
          logo={logoFor(current.appSlug)}
          companyName={ws.companyName}
          initialTab={panel?.tab}
          initialAction={panel?.action ?? null}
          canViewActivity={companyAdmin}
          feedback={feedback}
          onClose={() => setPanel(null)}
          onTryInChat={sendToChat}
          onOpenActivity={() => {
            setPanel(null);
            setActivity(true);
          }}
        />
      )}
      {assistants && features?.knowledgeMcp && (
        <OtherAssistantsSheet onClose={() => setAssistants(false)} />
      )}
      {shopify.dialog}
      {managedDatabase && (
        <NativePostgresSetup
          open
          existing={managedDatabase}
          companyName={ws.companyName}
          canCompany={companyAdmin}
          onClose={() => setManagedDatabase(null)}
          onSaved={(updated) => {
            databases.update(updated);
            setManagedDatabase(null);
          }}
          onDeleted={(message) => {
            databases.drop(managedDatabase.connectionId);
            setManagedDatabase(null);
            setNotice(message ?? copy.noticeDatabaseRemoved);
          }}
        />
      )}
      <PageHeading
        eyebrow={formatCopy(copy.eyebrow, { company: ws.companyName })}
        title={copy.title}
        description={copy.intro}
      >
        {companyAdmin && (
          <button className="ws-text-button" onClick={() => setActivity(!activity)}>
            {activity ? copy.backToConnections : copy.activity}
          </button>
        )}
      </PageHeading>
      {activity ? (
        <ConnectionActivity />
      ) : (
        <>
          {!current && feedback}
          <YourConnections
            rows={rows}
            // Nothing is said to be empty until every source has answered.
            loading={
              rows.length === 0 &&
              ((!data && !connections.error) ||
                (!custom.loaded && !custom.loadError) ||
                (databases.items === null && !databases.failed))
            }
            problems={[
              ...(connections.error
                ? [{ key: 'accounts', message: copy.yoursLoadError, onRetry: connections.retry }]
                : []),
              ...(custom.loadError
                ? [
                    {
                      key: 'servers',
                      message: copy.serversLoadError,
                      onRetry: () => void custom.refresh(),
                    },
                  ]
                : []),
              ...(databases.failed
                ? [
                    {
                      key: 'databases',
                      message: copy.databasesLoadError,
                      onRetry: () => void databases.load(),
                    },
                  ]
                : []),
            ]}
            words={words}
            busyKey={connections.busy ?? (custom.busy ? `server:${custom.busy}` : null)}
            logoFor={logoFor}
            onOpen={(row) => openPanel(row, row.state === 'review' ? 'access' : undefined)}
            onSignIn={signInFor}
            emptyText={
              data && !data.setup.available && !data.pipedreamAvailable
                ? connectionsCopy.accountConnectionsUnavailable
                : undefined
            }
          />
          {data?.pipedreamAvailable && pipedreamSetup && !pipedreamSetup.available && (
            <div className="ws-connection-notice ws-connection-provider-notice" role="alert">
              <p>{connectionAvailabilityMessage(pipedreamSetup.reason, locale)}</p>
            </div>
          )}
          <PipedreamCatalogue
            onConnect={connections.activate}
            busy={connections.busy}
            canConnect={Boolean(pipedreamSetup?.available)}
            companyName={ws.companyName}
            connectionFor={connectionFor}
            onOpenCustomerApps={(items) => {
              const choosable = items.filter((item) => item.actions.connect.available);
              const first = choosable.find((item) => item.appSetup?.useCase !== 'ads-mcp');
              setCustomerApp({
                items,
                initialId: (first ?? choosable[0] ?? items[0]).id,
                started: null,
              });
            }}
            pipedreamAvailable={Boolean(data?.pipedreamAvailable)}
            nativeItems={nativeOffers}
            onActivateNative={connections.activate}
            openNative={
              /[?&]setup=postgresql(?:&|$)/u.test(window.location.search) ||
              /[?&]setup=postgresql(?:&|$)/u.test(window.location.hash)
            }
            onNativeSaved={databases.update}
            onTryInChat={sendToChat}
            scopeKey={`${company?.company?.id ?? ''}:${company?.membership?.userId ?? ''}`}
            refreshKey={catalogueRefresh}
            onConnectionAdded={(message) =>
              setCustomMcpRefresh((current) => ({ version: current.version + 1, notice: message }))
            }
            onOpenShopify={shopify.open}
            otherWays={{
              canAddServer: custom.canConnect,
              onAddServer: custom.openAdd,
              onOpenAssistants: features?.knowledgeMcp ? () => setAssistants(true) : undefined,
            }}
            footer={
              <div className="ws-conn-ask">
                <span>{copy.askBody}</span>
                <button
                  type="button"
                  className="ws-conn-btn"
                  onClick={() => ws.send(copy.askPrompt)}
                >
                  <WorkspaceIcon name="chat" size={15} />
                  {copy.askAction}
                </button>
              </div>
            }
          />
          {custom.dialog}
        </>
      )}
    </div>
  );
}
